<script setup>
import { computed } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import { useCurrency } from '@/composables/useCurrency.js'
import UiModal from '@/Components/UI/UiModal.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import UiSelect from '@/Components/UI/UiSelect.vue'
import UiTextarea from '@/Components/UI/UiTextarea.vue'

const props = defineProps({
  driverId:   { type: Number, required: true },
  deliveries: { type: Array,  required: true },
})
const emit = defineEmits(['close'])
const { t } = useI18n()
const { formatMoney, formatDate } = useCurrency()

const form = useForm({
  delivery_ids: props.deliveries.map(d => d.id),
  method:       'cash',
  notes:        '',
})

const total = computed(() =>
  props.deliveries
    .filter(d => form.delivery_ids.includes(d.id))
    .reduce((s, d) => s + d.fee_cents, 0)
)

const methodOptions = [
  { value: 'cash',          label: 'Cash' },
  { value: 'card',          label: 'Card' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'online',        label: 'Online' },
]

function submit() {
  form.transform(d => ({ driver_payout: d }))
      .post(`/drivers/${props.driverId}/payouts`, {
        onSuccess: () => emit('close'),
      })
}
</script>

<template>
  <UiModal
    :open="true"
    :title="t('drivers.show.earnings.settleNow')"
    @close="emit('close')"
  >
    <div class="space-y-4">
      <div class="space-y-2">
        <label
          v-for="d in deliveries"
          :key="d.id"
          class="flex items-center gap-3 text-sm cursor-pointer"
        >
          <input
            v-model="form.delivery_ids"
            :value="d.id"
            type="checkbox"
            class="rounded"
          >
          <span class="flex-1 text-[color:var(--ink)]">
            {{ d.kind === 'handover' ? 'Handover' : 'Pickup' }}
            <span class="text-[color:var(--muted)]">
              · {{ formatDate(d.scheduled_for) }}
              <template v-if="d.address"> · {{ d.address }}</template>
            </span>
          </span>
          <span class="fw-value tabular-nums text-[color:var(--ink)]">{{ formatMoney(d.fee_cents) }}</span>
        </label>
      </div>

      <div>
        <p class="text-xs text-[color:var(--muted)] mb-1">{{ t('deliveries.show.method') }}</p>
        <UiSelect
          v-model="form.method"
          :options="methodOptions"
        />
      </div>

      <div>
        <p class="text-xs text-[color:var(--muted)] mb-1">{{ t('deliveries.show.notes') }}</p>
        <UiTextarea
          v-model="form.notes"
          :rows="2"
        />
      </div>

      <div class="flex justify-between items-center pt-2 border-t border-[color:var(--line)]">
        <span class="text-sm text-[color:var(--muted)]">{{ t('drivers.show.earnings.total') }}</span>
        <span class="fw-value tabular-nums text-[color:var(--ink)]">{{ formatMoney(total) }}</span>
      </div>
    </div>

    <template #footer>
      <UiButton
        variant="secondary"
        @click="emit('close')"
      >
        {{ t('common.cancel') }}
      </UiButton>
      <UiButton
        variant="primary"
        :disabled="form.processing || form.delivery_ids.length === 0"
        @click="submit"
      >
        {{ t('drivers.show.earnings.settleNow') }} {{ formatMoney(total) }}
      </UiButton>
    </template>
  </UiModal>
</template>
